import { saveAs } from "file-saver";
import moment from "moment";
import { convertCurrency } from ".";
import { convertOrderStatus, convertOrderType } from "./orderStatus";

// Header colour is the solid start colour of COLOR_APP
// (linear-gradient #1A66BE → #5A54A2). A gradient fill renders in Excel but is dropped
// by Google Sheets and Numbers, which would leave white header text on a white cell.
const HEADER_FILL = "FF1A66BE";
const HEADER_FONT_COLOR = "FFFFFFFF";
const BORDER_COLOR = "FFD9D9D9";

// The app renders everything in "FontLao2" (index.css), which is src/fonts/NotoSansLao/BB-Font.ttf.
// That file's real family name is "BoonBaan" — Excel needs the installed family name, not
// the CSS alias. xlsx cannot embed fonts, so machines without BoonBaan fall back to their own.
const FONT_NAME = "BoonBaan";

const INVOICE_STATUS_TEXT = {
  WAITING: "ຍັງບໍ່ທັນແຈ້ງຖອນ",
  REQUEST: "ແຈ້ງຖອນແລ້ວ",
  REJECT: "ຖືກປະຕິເສດ",
  APPROVED: "ຢືນຢັນການຖອນແລ້ວ",
};

const COLUMNS = [
  { header: "ລຳດັບ", key: "no", width: 8 },
  { header: "ເລກໃບສັ່ງຊື້", key: "code", width: 16 },
  { header: "ອາຟຣິລີເອດ", key: "affiliate", width: 22 },
  { header: "ປະເພດ", key: "type", width: 14 },
  { header: "ສະຖານະອໍເດີ້", key: "status", width: 18 },
  { header: "ຈຳນວນເງີນ (ກີບ)", key: "amount", width: 16, numFmt: "#,##0" },
  { header: "ການຊຳລະ", key: "paid", width: 14 },
  { header: "ເປີເຊັນຄອມມິດຊັ່ນ", key: "percent", width: 16, numFmt: '0"%"' },
  { header: "ເງີນຄອມມິດຊັ່ນ (ກີບ)", key: "commission", width: 18, numFmt: "#,##0" },
  { header: "ວັນທີສັ່ງຊື້", key: "createdAt", width: 18 },
  { header: "ລາຍການ", key: "items", width: 50 },
  { header: "ແຈ້ງຖອນ", key: "invoiceStatus", width: 18 },
];

const isAffiliateOrder = (item) => item?.typeForAffiliate === "ORDER_AFFILIATE";

// Same formula as handleCommission in CreateInvoice.js, kept numeric so Excel can sum it.
const commissionOf = (item) => {
  if (!isAffiliateOrder(item)) return 0;
  const commission = item?.sumPrice * (parseInt(item?.infulancer_percent) / 100);
  return Number.isFinite(commission) ? commission : 0;
};

const productNameOf = (order) => {
  const stocks = Array.isArray(order?.stocks) ? order.stocks[0] : order?.stocks;
  return order?.productName || order?.stock?.name || stocks?.name || "-";
};

const itemsText = (orders) =>
  (orders || [])
    .map(
      (order, index) =>
        `${index + 1}. ${productNameOf(order)} x${order?.amount || 0} @ ${order?.price || 0} ${convertCurrency(order?.currency || "₭")}`
    )
    .join("\n");

const toRow = (item, index) => ({
  no: index + 1,
  code: item?.code || "-",
  affiliate:
    `${item?.infulancer?.first_name || item?.affiliateName || "-"} ${item?.infulancer?.last_name || ""}`.trim(),
  type: convertOrderType(item?.type) || item?.type || "-",
  status: convertOrderStatus(item?.status ?? "-") || item?.status || "-",
  amount: Number(item?.sumPrice) || 0,
  paid: item?.isPaymented ? "ຊຳລະ" : "ຍັງບໍ່ຊຳລະ",
  percent: isAffiliateOrder(item) ? Number(item?.infulancer_percent) || 0 : 0,
  commission: commissionOf(item),
  createdAt: item?.createdAt ? moment(item.createdAt).format("DD/MM/YYYY HH:mm") : "-",
  items: itemsText(item?.orders),
  invoiceStatus: INVOICE_STATUS_TEXT[item?.invoiceStatus || "WAITING"] || item?.invoiceStatus,
});

/**
 * Build and download the invoice order list as .xlsx.
 *
 * exceljs is loaded on click only, so the ~1MB library never lands in the main bundle.
 */
export const exportInvoiceOrdersExcel = async ({ orderGroups, title, fileName }) => {
  const exceljsModule = await import("exceljs");
  const ExcelJS = exceljsModule.default || exceljsModule;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Orders", {
    views: [{ state: "frozen", ySplit: 2 }],
  });

  const lastColumn = COLUMNS.length;
  const baseFont = { name: FONT_NAME, size: 11 };
  const thinBorder = {
    top: { style: "thin", color: { argb: BORDER_COLOR } },
    left: { style: "thin", color: { argb: BORDER_COLOR } },
    bottom: { style: "thin", color: { argb: BORDER_COLOR } },
    right: { style: "thin", color: { argb: BORDER_COLOR } },
  };

  // Row 1: title across every column
  sheet.mergeCells(1, 1, 1, lastColumn);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { ...baseFont, size: 14, bold: true };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  sheet.getRow(1).height = 26;

  // Row 2: header in the app's table-header colour
  const headerRow = sheet.getRow(2);
  COLUMNS.forEach((column, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = column.header;
    cell.font = { ...baseFont, bold: true, color: { argb: HEADER_FONT_COLOR } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = thinBorder;
    sheet.getColumn(index + 1).width = column.width;
  });
  headerRow.height = 24;

  // Data rows
  const rows = (orderGroups || []).map(toRow);
  rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(rowIndex + 3);
    COLUMNS.forEach((column, index) => {
      const cell = excelRow.getCell(index + 1);
      cell.value = row[column.key];
      cell.font = baseFont;
      cell.border = thinBorder;
      cell.alignment = { vertical: "top", wrapText: column.key === "items" };
      if (column.numFmt) cell.numFmt = column.numFmt;
    });
  });

  // Total row
  const totalRow = sheet.getRow(rows.length + 3);
  const sum = (key) => rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
  const totals = { code: "ລວມ", amount: sum("amount"), commission: sum("commission") };
  COLUMNS.forEach((column, index) => {
    const cell = totalRow.getCell(index + 1);
    cell.value = totals[column.key] ?? "";
    cell.font = { ...baseFont, bold: true };
    cell.border = thinBorder;
    if (column.numFmt && totals[column.key] !== undefined) cell.numFmt = column.numFmt;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
  return rows.length;
};
